import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { CartProvider } from './context/CartContext';
import { SupabaseProvider } from './context/SupabaseContext';
import { AdminProvider } from './context/AdminContext';
import { Layout } from './components/Layout';
import { AdminLayout } from './components/AdminLayout';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy Load Pages
const Home = React.lazy(() => import('./pages/Home'));
const Menu = React.lazy(() => import('./pages/Menu'));
const Reservations = React.lazy(() => import('./pages/Reservations'));
const OrderOnline = React.lazy(() => import('./pages/OrderOnline'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Reviews = React.lazy(() => import('./pages/Reviews'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// Lazy Load Admin Pages (Named Exports)
const AdminLogin = React.lazy(() => import('./pages/admin/Login').then(module => ({ default: module.AdminLogin })));
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard').then(module => ({ default: module.AdminDashboard })));
const OrderManager = React.lazy(() => import('./pages/admin/OrderManager').then(module => ({ default: module.OrderManager })));
const ReservationManager = React.lazy(() => import('./pages/admin/ReservationManager').then(module => ({ default: module.ReservationManager })));
const MenuManager = React.lazy(() => import('./pages/admin/MenuManager').then(module => ({ default: module.MenuManager })));
const ReviewManager = React.lazy(() => import('./pages/admin/ReviewManager').then(module => ({ default: module.ReviewManager })));
const StaffManager = React.lazy(() => import('./pages/admin/StaffManager').then(module => ({ default: module.StaffManager })));

const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-mocha-light">
    <Loader2 className="animate-spin text-terracotta w-10 h-10 mb-4" />
    <p className="font-serif text-gray-500 animate-pulse">Loading Zayka Palace...</p>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <SupabaseProvider>
        <AdminProvider>
          <CartProvider>
            <Router>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />

                  <Route path="/admin/*" element={
                    <AdminLayout>
                      <Routes>
                        <Route index element={<AdminDashboard />} />
                        <Route path="orders" element={<OrderManager />} />
                        <Route path="reservations" element={<ReservationManager />} />
                        <Route path="menu" element={<MenuManager />} />
                        <Route path="reviews" element={<ReviewManager />} />
                        <Route path="staff" element={<StaffManager />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </AdminLayout>
                  } />

                  {/* Customer Routes */}
                  <Route path="/*" element={
                    <Layout>
                      <Routes>
                        <Route index element={<Home />} />
                        <Route path="menu" element={<Menu />} />
                        <Route path="reservations" element={<Reservations />} />
                        <Route path="order" element={<OrderOnline />} />
                        <Route path="about" element={<About />} />
                        <Route path="contact" element={<Contact />} />
                        <Route path="reviews" element={<Reviews />} />
                        <Route path="faq" element={<FAQ />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Layout>
                  } />
                </Routes>
              </Suspense>
            </Router>
          </CartProvider>
        </AdminProvider>
      </SupabaseProvider>
    </ErrorBoundary>
  );
}

export default App;
